import React, { createContext, useContext, useEffect, useState } from 'react';
import { Loan, Task, TaskState } from 'gql/graphql';
import { PlusCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

import CardOwnership from './CardOwnership/CardOwnership';
import { TaskContext } from '../EntityGuarantorCreation';

// Contexto para compartir getTotal con componentes hijos
import SectionErrorTask from 'scenes/Private/components/SectionErrorTask/SectionErrorTask';
import SectionSuccessTask from 'scenes/Private/components/SectionSuccessTask/SectionSuccessTask';
import CollapseIssues from 'scenes/Private/components/CollapseIssues/CollapseIssues';
import ButtonSubmit from '../../../../../../components/Atoms/Buttons/ButtonSubmit/ButtonSubmit';
import { useCreateResponseTask } from '../../../../../../services/task/mutations';
import DropdownBlue from '../../../../../../components/Atoms/Dropdown/DropdownBlue/DropdownBlue';
import { useGetTaskResponsesByTask } from '../../../../../../services/task/querys';
import CardOwnershipNew from '../Step2/CardOwnershipNew/CardOwnershipNew';
import { capitalizeNames } from '../../../../../../@common/utils/formatters/formatter';
import { useCreateOwnership } from '../../../../../../services/ownership/mutations';
import useGuarantorUtil from '../useGuarantorUtil';
import { LoanContext } from '../../../Dashboard';
import { useGetOwnershipsByCreator } from '../../../../../../services/ownership/querys';
import ModalFinish from '../ModalFinish';
import { DATE_FORMAT } from '@common/utils/constants/dates.constanst';

export const TotalPercentageContext = createContext<() => number>(() => 0);

interface Step3Props {
  setStep: Function;
  closeTask: Function;
  responsesStep2: any[];
  responseStep1: any;
  entity: any;
}

const Step3 = ({ setStep, responseStep1, responsesStep2, closeTask, entity }: Step3Props) => {
  const task: Task | any = useContext(TaskContext);
  const loan: Loan | any = useContext(LoanContext);

  const { data: responseCreateOwner, execute: fetchCreateOwnership } = useCreateOwnership();
  const { data: responseCreateTask, fetching, execute: createResponseTask } = useCreateResponseTask();
  const { data: ownershipsCreated, reexecute: getOwnershipsByCreator } = useGetOwnershipsByCreator({});
  
  const { reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } }
  });

  const [newOwner, setNewOwner] = useState<any>();
  const [ownerships, setOwnerships] = useState<any>([]);
  const [taskCompleted, setTaskCompleted] = useState<boolean>();
  const [createPending, setCreatePending] = useState<any>();
  const [ownersExpand, setOwnersExpand] = useState<any>([]);
  const [newPercentageLiveEdit, setNewPercentageLiveEdit] = useState<any>();
  const [ownerPercentageLiveEdit, setOwnerPercentageLiveEdit] = useState<any>();
  const [modalFinish, setModalFinish] = useState<boolean>(false);
  const [isSubmittedForReview, setIsSubmittedForReview] = useState<boolean>(false);

  const { getGuarantorsNameElements, getTotal, getSomeoneNews, selectOwnersCache } = useGuarantorUtil({ 
    ownerships, 
    ownershipsUserCreator: ownershipsCreated, 
    newPercentageLiveEdit, 
    ownerPercentageLiveEdit 
  });

  useEffect(() => {
    if (ownersExpand && ownersExpand?.length === 0) {
      let _ownersExpand = [];
      for (const owner of ownerships) {
        if (owner.state === TaskState.Incomplete || owner.state === TaskState.RequestError) {
          _ownersExpand.push(owner._id || owner.ownerId);
        }
      }
      setOwnersExpand(_ownersExpand);
    }
  }, [ownerships])

  useEffect(() => {
    if (responseCreateOwner && createPending) {
      const ownerId = responseCreateOwner._id;
      const payload = createPending;

      payload.metadata.ownerId = ownerId;
      createResponseTask(payload);
      setCreatePending(undefined);
      if (payload.metadata.ownership >= 20)
        setOwnersExpand([...ownersExpand, ownerId]);
    }
  }, [responseCreateOwner])

  useEffect(() => {
    if (responseCreateTask) {
      setNewPercentageLiveEdit(null);
      setNewOwner(null);
      getTaskResponses({ variables: { taskId: task._id }, requestPolicy: 'network-only' });
    }
  }, [responseCreateTask]);

  useEffect(() => {
    const _ownerships = [];

    if (responsesStep2?.length > 0) {
      for (const taskResponse of responsesStep2) {
        const { loans: ignoreLoans, ...taskResponseFiltered } = taskResponse;
        const { loans: ignoreLoans2, ...metadataFiltered } = taskResponse.metadata;
        const { _id, loans, ...modelOwner } = ownershipsCreated?.find(item => item._id === taskResponse.metadata.ownerId) || {};

        _ownerships.push({
          ...modelOwner,
          key: taskResponse._id,
          state: taskResponse.state,
          files: taskResponse.files,
          ...metadataFiltered,
          taskResponse: taskResponseFiltered
        });
      }
      setTaskCompleted(responsesStep2.every(item => item.state === TaskState.Approved));
      setOwnerships(_ownerships);
    }
  }, [responsesStep2]);
  
  const disabledSubmitReview = () => {
    const hasIncomplete = ownerships.some((item: any) => item.state === TaskState.Incomplete);
    const atLeastOneReview = ownerships.some((item: any) => item.state === TaskState.Review);
    const allApproved = ownerships.every((item: any) => item.state === TaskState.Approved);
    const atLeastOneRejected = ownerships.some((item: any) => item.state === TaskState.RequestError);
    
    // Si hay incomplete pero la tarea está en Review Y hay al menos 1 DRAFT, NO deshabilitar
    const atLeastOneDraft = ownerships.some((item: any) => item.state === TaskState.Draft);
    if(task.state === TaskState.Review && atLeastOneDraft){
      return false;
    }
    
    return hasIncomplete || (atLeastOneReview && task.state !== TaskState.RequestError) || allApproved || atLeastOneRejected;
  };

  const submitForReview = async () => {
    setIsSubmittedForReview(true);
    for (const ownership of ownerships) {
      if (ownership.taskResponse.state === TaskState.Draft) {
        const {
          files: ignoreFile,
          task,
          __typename,
          createdAt,
          ...taskResponse
        } = ownership.taskResponse;

        await createResponseTask({
          ...taskResponse,
          task: task._id,
          state: TaskState.Review,
          metadata: taskResponse.metadata,
        });
      }
    }
    setModalFinish(true);
  }

  const createOwnership = async (values: any, state?: TaskState) => {
    const { files, ...ownership } = values;

    ownership.name = capitalizeNames(ownership.name);
    ownership.lastname = capitalizeNames(ownership.lastname);

    const ownerId =
      responseStep1.metadata.ownerId &&
        responseStep1.metadata.ownerId !== 'someone_new'
        ? responseStep1.metadata.ownerId
        : undefined;

    const metadata = responsesStep2.length === 0
      ? { _id: ownerId, ...ownership }
      : ownership;

    let payload: any = {
      task: task._id,
      key: `ownership-${values.name}`,
      label: `Ownership ${values.name}`,
      step: 2,
      metadata,
      state: state ? state : ownership.ownership >= 20 ? TaskState.Incomplete : TaskState.Draft
    };

    //Es un archivo recien subido
    const isFile = files?.some((item: any) => item.size);

    if (isFile) payload.files = files;
    else payload.filesExist = files;

    if (ownership.ownership >= 20) {
      await fetchCreateOwnership({
        ...metadata,
        entity: entity._id,
        loan: loan._id,
        state: TaskState.Incomplete,
        stakes: {
          [entity._id]: ownership.ownership
        }
      });
      setCreatePending(payload);
    } else {
      //Cuando se crea desde el dropdown, se crea sin porcentaje
      const modelOwner = ownershipsCreated?.find(item => item._id === ownership._id)

      payload.state = modelOwner ? TaskState.Incomplete : TaskState.Draft;

      let payloadOwner: any = {
        ...ownership,
        entity: entity._id,
        loan: loan._id,
      }

      if (modelOwner) {
        payloadOwner._id = modelOwner._id;
        payloadOwner.stakes = {
          ...modelOwner.stakes,
          [entity._id]: ownership.ownership
        };
      } else
        payloadOwner.stakes = { [entity._id]: ownership.ownership };

      await fetchCreateOwnership(payloadOwner);
      setCreatePending(payload);
    }
  };

  const handlerSwitchExpandCard = (ownerId: string, isClose?: boolean) => {
    if (ownersExpand.includes(ownerId) || isClose) {
      let removeOwner = ownersExpand.filter((item: string) => item !== ownerId)
      setOwnersExpand(removeOwner);
    } else {
      setOwnersExpand([...ownersExpand, ownerId])
    }
  }

  const itemCardOwner = (ownership: any, index: number) => {
    const isExpand = ownersExpand.find((item: string) =>
      ownership.ownership >= 20 &&
      (item === ownership?._id || item === ownership?.ownerId) 
    )

    const isDisabled = ownership.state !== TaskState.RequestError &&
      ownership.state !== TaskState.Draft &&
      ownership.state !== TaskState.Incomplete

    return (
      <CardOwnership
        key={index}
        index={index}
        ownership={ownership}
        disabledForm={isDisabled}
        ownerships={ownerships}
        closeTask={closeTask}
        isActionRemoved={
          !isSubmittedForReview &&
          task.state !== TaskState.Review &&
          ownership.state !== TaskState.Review &&
          ownership.state !== TaskState.RequestError &&
          ownership.state !== TaskState.Approved
        }
        entity={entity}
        onDeleteOwner={() => getOwnershipsByCreator({ requestPolicy: 'network-only' })}
        isExpand={isExpand}
        handlerSwitchExpandCard={handlerSwitchExpandCard}
        setNewPercentageLiveEdit={setOwnerPercentageLiveEdit}
      />
    );
  }

  const isDisabledSomeoneNew = () => {

    const isTaskReview = task?.state === TaskState.Review;
    const isTaskApproved = task?.state === TaskState.Approved;

    return newOwner || (isTaskReview && getTotal() >= 100) || isTaskApproved;
  }

  return (
    <TotalPercentageContext.Provider value={getTotal}>
      <section className="step-3">
        <div className="section-1">
          {ownerships && (
            <h3 className="h2">
              <>
                <span>{`We've added `}</span>
                <label>{getGuarantorsNameElements()}</label>
                {ownerships.length === 1 && <span>{" as a guarantor on the loan. Let's get their profile filled out"}</span>}
                {ownerships.length > 1 && <span>{" as guarantors on the loan. Let's get their profiles filled out"}</span>}
              </>
            </h3>
          )}

          {taskCompleted &&
            <SectionSuccessTask
              title="You've completed this task."
              button={{ title: "Back to dashboard", onClick: closeTask }}
            />
          }

          {!taskCompleted &&
            <SectionErrorTask
              taskId={task._id}
              componentDataError={(textDataError: any) => (
                <div className='text-data'>
                  <div className='row'>
                    <span>SSN</span>
                    <b>{textDataError.ssn}</b>
                  </div>
                  <div className='row'>
                    <span>Date of birth</span>
                    <b>{moment(textDataError.dateBirth).format(DATE_FORMAT)}</b>
                  </div>
                  <div className='row'>
                    <span>Phone number</span>
                    <b>{textDataError.phone}</b>
                  </div>
                  <div className='row'>
                    <span>Email address</span>
                    <b>{textDataError.email}</b>
                  </div>
                  <div className='row'>
                    <span>Home address</span>
                    <b>{textDataError.address}</b>
                  </div>
                </div>
              )}
            />
          }

          <div className="content-cards mt-30">

            {ownerships.map((ownership: any, index: number) =>
              itemCardOwner(ownership, index))
            }

            {newOwner && (
              <CardOwnershipNew
                ownership={newOwner}
                totalPercentage={getTotal()}
                disabledNames={ownerships.length === 0 && newOwner.name}
                onSaveDraft={(v) => createOwnership(v)}
                handlerClose={() => setNewOwner(null)}
                tagStateGuarantor={"incomplete"}
                setNewPercentageLiveEdit={setNewPercentageLiveEdit}
              />
            )}
          </div>

          <div className="content-owner-new mt-10">
            <div className="text-add">
              <PlusCircleOutlined />
              <span>Add</span>
              <DropdownBlue
                label="someone new"
                disabled={isDisabledSomeoneNew()}
                className={`${newOwner && 'disabled'}`}
                items={getSomeoneNews(!!newOwner)}
                onClickSelect={(key: string) => selectOwnersCache(key, setNewOwner, createOwnership)}
              />
              <span>as an owner</span>
            </div>
            <div className={`total ${getTotal() !== 100 && 'total-error'}`}>
              <span>Total</span>
              <b>{getTotal()}%</b>
            </div>
          </div>

          <div className='mt-15'>
            <ButtonSubmit
              loading={false}
              modalConfirm={{ show: true }}
              onClick={submitForReview}
              messageErrorDisabled='Add 100% ownership to submit entity for review'
              disabled={disabledSubmitReview() || getTotal() !== 100 || newOwner}
              showErrorBox={getTotal() !== 100}
            >
              {disabledSubmitReview() || getTotal() !== 100 || newOwner ?
                'Add 100% ownership to submit entity for review' :
                'Submit entity and profiles for review'
              }
            </ButtonSubmit>
          </div>


          <CollapseIssues taskId={task._id} taskKey={task.key} rejectionCode='no_action_required' />
        </div>
        <ModalFinish
          visible={modalFinish}
          setVisible={setModalFinish}
          closeTask={closeTask}
        />
      </section>
    </TotalPercentageContext.Provider>
  );
};
export default Step3;
